import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { connect } from 'react-redux';
import { Box, Button, Text, ShadowBox } from '@components/common';
import DefaultLoader from '@components/loading/DefaultLoader';
import ScheduledCheckoutItem from '@containers/booking/Confirm/RecurringCheckoutForm/ScheduledCheckoutItem';
import PaymentSelection from '@containers/booking/Confirm/PaymentSelection';
import TableCell from '@components/TableCell';
import { renderServiceItem } from '@containers/booking/Confirm/commonRenders';

import { AppSelectors } from '@redux/AppRedux';
import { ScheduleSelectors } from '@redux/ScheduleRedux';
import { BookingSelectors } from '@redux/BookingRedux';
import SubscriptionActions, {
  SubscriptionSelectors
} from '@redux/SubscriptionRedux';

import { getErrors } from '@utils/error';
import API from '@utils/api';
import {
  transformSchedule,
  transfromPropertyToAddress
} from '@services/transform';

class ModifyCheckoutForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      costs: [],
      isCalculating: true,
      costPayload: {},
      isSubmitting: false,
      errorMsg: ''
    };
  }

  async componentDidMount() {
    const {
      scheduledItems,
      currentPackage,
      cycleLength,
      property
    } = this.props;
    const { propertyID } = property || {};
    try {
      const costPayload = {
        package: currentPackage,
        cycle: scheduledItems,
        cycleLength,
        propertyID
      };

      const pp = await API.getRecurringCostCalculations(costPayload);
      this.setState({ costs: pp.result, isCalculating: false, costPayload });
    } catch (err) {
      this.setState({ isCalculating: false });
    }
  }

  handleCostUpdate = newCost => {
    this.setState({ costs: newCost });
  };

  renderAddons = (addons, appData) => {
    if (!addons) {
      return null;
    }
    return addons.map(addOn => {
      const addonData = appData.addons[addOn];
      const { price, title } = addonData;

      return (
        <TableCell
          key={addOn}
          title={title}
          rightText={price.title}
        ></TableCell>
      );
    });
  };

  renderScheduledItem = (
    scheduleItem,
    itemCost,
    scheduleData,
    paymentMethod
  ) => {
    const { appData, cycleLength, property } = this.props;
    const {
      week,
      day,
      additionalService,
      serviceType,
      timeframe
    } = scheduleItem;
    const { cost: totalCost, discountDetails, couponDetails } = itemCost || {};

    if (!serviceType) {
      return null;
    }

    const { serviceDate, rescheduleItem } = scheduleData;

    if (!serviceDate) {
      return null;
    }
    let { momentDate } = serviceDate;

    if (rescheduleItem) {
      const { rescheduleDate, skip } = rescheduleItem || {};
      if (skip) {
        momentDate = momentDate.clone().add(cycleLength, 'week');
      } else {
        momentDate = rescheduleDate.momentDate.clone();
      }
    } else if (momentDate.isBefore(moment(), 'day')) {
      momentDate = momentDate.clone().add(cycleLength, 'week');
    }

    return (
      <ShadowBox mb={4} key={`${serviceType}_${week}_${day}`}>
        {renderServiceItem(
          appData,
          property,
          serviceType,
          additionalService,
          momentDate,
          totalCost,
          discountDetails,
          couponDetails,
          paymentMethod,
          timeframe
        )}
      </ShadowBox>
    );
  };

  renderScheduledPayments = formValues => {
    const { scheduledItems, appData, selectedItems, property } = this.props;
    const { costs } = this.state;

    const { paymentMethod } = formValues || {};
    const fullAddress = transfromPropertyToAddress(property);

    return (
      <Box display="flex" flexDirection="column" alignItems="stretch">
        <Text fontSize={1} ml={3} mb={3}>
          Your Service
        </Text>
        <Text fontSize={1} ml={3} mb={3}>
          {fullAddress}
        </Text>
        {scheduledItems.map((item, index) => (
          <ScheduledCheckoutItem
            key={index}
            data={item}
            refData={appData}
            scheduleData={selectedItems[index]}
            costData={costs}
            renderDetail={this.renderScheduledItem}
            paymentMethod={paymentMethod}
          />
        ))}
      </Box>
    );
  };

  handleSubmit = async () => {
    const {
      cycleLength,
      currentPackage,
      scheduledItems,
      loadSubscriptions,
      onSubmit,
      activeSubscriptionId,
      property
    } = this.props;
    const { propertyID } = property || {};

    this.setState({
      isSubmitting: true
    });

    try {
      await API.updateRecurringBooking(activeSubscriptionId, {
        package: currentPackage,
        cycle: scheduledItems,
        cycleLength,
        propertyID
      });

      loadSubscriptions();
      onSubmit();
    } catch (err) {
      const error = getErrors(err);

      if (error.message) {
        this.setState({
          errorMsg: error.message
        });
      } else {
        this.setState({
          errorMsg:
            Object.values(error).pop() ||
            'There was an error in creating service'
        });
      }

      this.setState({
        isSubmitting: false
      });
      return;
    }
    this.setState({
      isSubmitting: false
    });
  };

  render() {
    const { onCancel } = this.props;
    const { isCalculating, isSubmitting, errorMsg } = this.state;

    if (isCalculating) {
      return (
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="Center"
        >
          <DefaultLoader size={64} />
        </Box>
      );
    }

    const formValues = { paymentMethod: 'credit' };

    return (
      <>
        <Box display="flex" flexDirection="column">
          <Text fontSize={1} ml={3} mb={3}>
            Payment Method
          </Text>
          <PaymentSelection selectedMethod="credit" mb={4} />
        </Box>

        {this.renderScheduledPayments(formValues)}

        {errorMsg && (
          <Text color="red" mb={4}>
            {errorMsg}
          </Text>
        )}

        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          mb={4}
        >
          <Button
            onClick={onCancel}
            type="button"
            variant="secondary"
            mr={4}
            p={3}
            flex={1}
          >
            Cancel
          </Button>
          <Button
            onClick={() => this.handleSubmit(formValues)}
            p={3}
            flex={1}
            disabled={isSubmitting}
          >
            Confirm
          </Button>
        </Box>
      </>
    );
  }
}

ModifyCheckoutForm.propTypes = {
  appData: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  loadSubscriptions: PropTypes.func,
  currentPackage: PropTypes.string,
  scheduledItems: PropTypes.array,
  selectedItems: PropTypes.array,
  cycleLength: PropTypes.number,
  activeSubscriptionId: PropTypes.any,
  property: PropTypes.object
};

const mapStatesToProps = state => ({
  appData: AppSelectors.selectAppData(state),
  cycleLength: ScheduleSelectors.selectNumOfRows(state),
  currentPackage: ScheduleSelectors.selectPackage(state),
  scheduledItems: ScheduleSelectors.selectScheduledItems(state).map(
    transformSchedule
  ),
  selectedItems: ScheduleSelectors.selectScheduledItems(state),
  activeSubscriptionId: SubscriptionSelectors.selectActiveSubscriptionId(state),
  property: BookingSelectors.selectCurrentProperty(state)
});

const mapDispatchToProps = dispatch => ({
  loadSubscriptions: () =>
    dispatch(SubscriptionActions.setSubscriptionLoadRequest())
});

const connectedForm = connect(
  mapStatesToProps,
  mapDispatchToProps
)(ModifyCheckoutForm);

export default connectedForm;
